import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { VoiceSession, VoiceState, VoiceCommand, VoiceResponse, MenuItem, OrderData, BookingData, CartData } from '../../types/voice';

const initialState: VoiceState = {
  isListening: false,
  isProcessing: false,
  isPlaying: false,
  currentSession: null,
  lastTranscript: '',
  lastResponse: '',
  transcript: '', // For backward compatibility
  conversationHistory: [], // For backward compatibility
  confidence: 0,
  error: null,
  connectionStatus: 'disconnected',
  microphonePermission: 'prompt',
  // WebSocket real-time data
  menuItems: [],
  currentCart: null,
  currentOrder: null,
  currentBooking: null,
  customerInfo: null,
};

// Async thunks
export const startVoiceSession = createAsyncThunk(
  'voice/startSession',
  async (data: { tableNumber?: string; customerId?: string }, { rejectWithValue }) => {
    try {
      // Add company from environment
      const company = process.env.REACT_APP_COMPANY || 'QASTCO';
      const requestData = { ...data, company };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/voice/start-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || localStorage.getItem('guestToken')}`
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error.message);
      }

      return await response.json();
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const processVoiceCommand = createAsyncThunk(
  'voice/processCommand',
  async (command: VoiceCommand, { rejectWithValue }) => {
    try {
      // Add company parameter
      const company = process.env.REACT_APP_COMPANY || 'QASTCO';
      const commandWithCompany = { ...command, company };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/voice/process-command`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || localStorage.getItem('guestToken')}`
        },
        body: JSON.stringify(commandWithCompany),
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error.message);
      }

      return await response.json();
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const endVoiceSession = createAsyncThunk(
  'voice/endSession',
  async (sessionId: string, { rejectWithValue }) => {
    try {
      // Add company parameter
      const company = process.env.REACT_APP_COMPANY || 'QASTCO';

      const response = await fetch(`${process.env.REACT_APP_API_URL}/voice/end-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || localStorage.getItem('guestToken')}`
        },
        body: JSON.stringify({ sessionId, company }),
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error.message);
      }

      return await response.json();
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const voiceSlice = createSlice({
  name: 'voice',
  initialState,
  reducers: {
    setListening: (state, action: PayloadAction<boolean>) => {
      state.isListening = action.payload;
    },
    setProcessing: (state, action: PayloadAction<boolean>) => {
      state.isProcessing = action.payload;
    },
    setPlaying: (state, action: PayloadAction<boolean>) => {
      state.isPlaying = action.payload;
    },
    setTranscript: (state, action: PayloadAction<string>) => {
      state.lastTranscript = action.payload;
      state.transcript = action.payload; // For backward compatibility
    },
    setConfidence: (state, action: PayloadAction<number>) => {
      state.confidence = action.payload;
    },
    setConnectionStatus: (state, action: PayloadAction<VoiceState['connectionStatus']>) => {
      state.connectionStatus = action.payload;
    },
    setMicrophonePermission: (state, action: PayloadAction<PermissionState>) => {
      state.microphonePermission = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetVoiceState: (state) => {
      state.isListening = false;
      state.isProcessing = false;
      state.isPlaying = false;
      state.lastTranscript = '';
      state.lastResponse = '';
      state.transcript = '';
      state.conversationHistory = [];
      state.confidence = 0;
      state.error = null;
    },
    // WebSocket real-time data actions
    setMenuItems: (state, action: PayloadAction<MenuItem[]>) => {
      state.menuItems = action.payload;
    },
    setCurrentCart: (state, action: PayloadAction<CartData | null>) => {
      state.currentCart = action.payload;
    },
    setCurrentOrder: (state, action: PayloadAction<OrderData | null>) => {
      state.currentOrder = action.payload;
      // Clear cart when order is created
      if (action.payload) {
        state.currentCart = null;
      }
    },
    setCurrentBooking: (state, action: PayloadAction<BookingData | null>) => {
      state.currentBooking = action.payload;
    },
    setCustomerInfo: (state, action: PayloadAction<any>) => {
      console.log('🔵 REDUX setCustomerInfo reducer called');
      console.log('   Previous customerInfo:', state.customerInfo);
      console.log('   New customerInfo payload:', action.payload);
      // Merge new data with existing data instead of replacing
      state.customerInfo = { ...state.customerInfo, ...action.payload };
      console.log('   Updated customerInfo (merged):', state.customerInfo);
      console.log('✅ REDUX customerInfo state updated');
    },
    clearWebSocketData: (state) => {
      state.menuItems = [];
      state.currentCart = null;
      state.currentOrder = null;
      state.currentBooking = null;
      state.customerInfo = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Start session
      .addCase(startVoiceSession.pending, (state) => {
        state.isProcessing = true;
        state.error = null;
      })
      .addCase(startVoiceSession.fulfilled, (state, action) => {
        state.isProcessing = false;
        // Handle both old format (session) and new format (sessionId + config)
        state.currentSession = action.payload.session || {
          sessionId: action.payload.sessionId,
          tableNumber: action.payload.tableNumber || '1',
          sessionStart: new Date().toISOString(),
        };
        state.connectionStatus = 'connected';
      })
      .addCase(startVoiceSession.rejected, (state, action) => {
        state.isProcessing = false;
        state.error = action.payload as string;
        state.connectionStatus = 'error';
      })
      // Process command
      .addCase(processVoiceCommand.pending, (state) => {
        state.isProcessing = true;
      })
      .addCase(processVoiceCommand.fulfilled, (state, action) => {
        state.isProcessing = false;
        state.lastResponse = action.payload.response.text;
        state.confidence = action.payload.confidence || 0;
      })
      .addCase(processVoiceCommand.rejected, (state, action) => {
        state.isProcessing = false;
        state.error = action.payload as string;
      })
      // End session
      .addCase(endVoiceSession.pending, (state) => {
        state.isProcessing = true;
      })
      .addCase(endVoiceSession.fulfilled, (state) => {
        state.isProcessing = false;
        state.currentSession = null;
        state.connectionStatus = 'disconnected';
        state.isListening = false;
      })
      .addCase(endVoiceSession.rejected, (state, action) => {
        state.isProcessing = false;
        state.error = action.payload as string;
      });
  },
});

// Helper actions for backward compatibility
export const toggleListening = (state: any) => (dispatch: any) => {
  dispatch(setListening(!state.voice.isListening));
};

export const stopVoiceSession = (sessionId: string) => (dispatch: any) => {
  dispatch(endVoiceSession(sessionId));
};

export const {
  setListening,
  setProcessing,
  setPlaying,
  setTranscript,
  setConfidence,
  setConnectionStatus,
  setMicrophonePermission,
  clearError,
  resetVoiceState,
  setMenuItems,
  setCurrentCart,
  setCurrentOrder,
  setCurrentBooking,
  setCustomerInfo,
  clearWebSocketData,
} = voiceSlice.actions;

export default voiceSlice.reducer;